import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@mui/material'
import { t } from 'i18n'
import React, { useEffect, useState } from 'react'
import { HeatmapDefinition } from 'store/ducks/report/types'

interface RenameHeatmapDialogProps {
  open: boolean
  heatmap?: HeatmapDefinition
  onClose: () => void
  onConfirm: (heatmapId: string, newName: string) => Promise<void>
}

const RenameHeatmapDialog: React.FC<RenameHeatmapDialogProps> = ({
  open,
  heatmap,
  onClose,
  onConfirm
}) => {
  const [newName, setNewName] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (heatmap) {
      setNewName(heatmap.name || heatmap.fileName)
    } else {
      setNewName('')
    }
  }, [heatmap])

  const handleConfirm = async () => {
    if (!heatmap || !newName.trim() || loading) return

    try {
      setLoading(true)
      await onConfirm(heatmap.id, newName.trim())
      onClose()
    } catch (error) {
      // Error handling is done in the parent hook
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('heatmaps.rename') || 'Rename Heatmap'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label={t('name') || 'Name'}
          value={newName}
          onChange={e => setNewName(e.target.value)}
          margin="normal"
          variant="outlined"
          disabled={loading}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!newName.trim() || loading}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {loading ? t('heatmaps.saving') || 'Saving...' : t('save') || 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RenameHeatmapDialog
